import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import { AppShellRightPanelConfig } from './app-shell.models';
import { ComponentBuilderInspectorComponent } from '../../components/component-studio/component-builder-inspector.component';
import { WebsiteBuilderInspectorComponent } from '../../components/website-builder/website-builder-inspector.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';

@Component({
  selector: 'app-right-panel',
  standalone: true,
  imports: [IonIcon, ComponentBuilderInspectorComponent, WebsiteBuilderInspectorComponent, InfoTooltipComponent],
  templateUrl: './app-right-panel.component.html',
  styleUrls: ['./app-right-panel.component.scss']
})
export class AppRightPanelComponent {
  private readonly desktopWidthStorageKey = 'bw-app-shell-right-panel-width';
  private readonly minDesktopWidth = 250;
  private readonly maxDesktopWidth = 560;

  @Input() config: AppShellRightPanelConfig | null = null;
  @Input() compact = false;
  @Input() open = false;
  @Output() toggleRequest = new EventEmitter<void>();
  @Output() closeRequest = new EventEmitter<void>();

  desktopWidth = this.restoreDesktopWidth();
  private resizing = false;

  constructor() {
    addIcons({ chevronBackOutline, chevronForwardOutline });
  }

  get desktopWidthStyle(): string {
    return `${this.desktopWidth}px`;
  }

  get toggleIcon(): string {
    return this.open ? 'chevron-forward-outline' : 'chevron-back-outline';
  }

  requestToggle(): void {
    this.toggleRequest.emit();
  }

  closeCompactPanel(): void {
    if (this.compact) {
      this.closeRequest.emit();
    }
  }

  startResize(event: PointerEvent): void {
    if (this.compact || !this.open) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.resizing = true;
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (!this.resizing || this.compact) {
      return;
    }

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
    const proposedWidth = viewportWidth - event.clientX;
    this.desktopWidth = clamp(proposedWidth, this.minDesktopWidth, this.maxDesktopWidth);
    this.persistDesktopWidth();
  }

  @HostListener('document:pointerup')
  handlePointerUp(): void {
    this.resizing = false;
  }

  private restoreDesktopWidth(): number {
    if (typeof window === 'undefined') {
      return 320;
    }

    const storedWidth = Number(window.localStorage.getItem(this.desktopWidthStorageKey));
    if (Number.isNaN(storedWidth) || !storedWidth) {
      return 320;
    }

    return clamp(storedWidth, this.minDesktopWidth, this.maxDesktopWidth);
  }

  private persistDesktopWidth(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.desktopWidthStorageKey, String(this.desktopWidth));
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
